import { useNavigate } from "react-router-dom";
import { openNotification } from "../Components/Notification/Index";
import { useAILoader } from "../Context/AILoaderContext/AILoaderContext";
import { useCurrentApplication } from "../Context/CurrentApplicationContext/CurrentApplicationContext";
import {
  JobApplication,
  JobApplicationStatus,
} from "../Entities/job-application.entity";
import { Userprofile } from "../Entities/user-profile.entity";
import {
  InterviewSession,
  MatchResult,
  ParsedResumeContent,
} from "../Interface/Index";
import InterviewService from "../Services/interview.service";
import JobApplicationService from "../Services/job.application.service";
import ProfileMatcherService from "../Services/profile.matcher.service";

const PROFILE_MATCHED_JOB_POST_ID_KEY = "profileMatchedJobPostId";
const IS_PROFILE_MATCHING_KEY = "isProfileMatching";
const PROFILE_MATCHED_APPLICATION_ID_KEY = "profileMatchedApplicationId";
const PROFILE_MATCHED_FILE_NAME_KEY = "profileMatchedFileName";
const PROFILE_MATCHED_PROFILE_ID_KEY = "profileMatchedProfileId";

function setProfileMatchStartedOnLocalStorage(
  jobPostId: string,
  applicationId: string,
  fileName: string | null,
  profileId: string | null,
) {
  localStorage.setItem(PROFILE_MATCHED_JOB_POST_ID_KEY, jobPostId);
  localStorage.setItem(IS_PROFILE_MATCHING_KEY, "true");
  localStorage.setItem(PROFILE_MATCHED_APPLICATION_ID_KEY, applicationId);
  localStorage.setItem(PROFILE_MATCHED_FILE_NAME_KEY, fileName || "");
  localStorage.setItem(PROFILE_MATCHED_PROFILE_ID_KEY, profileId || "");
}

function clearProfileMatchTrackOnLocalStorage() {
  localStorage.removeItem(PROFILE_MATCHED_JOB_POST_ID_KEY);
  localStorage.removeItem(IS_PROFILE_MATCHING_KEY);
  localStorage.removeItem(PROFILE_MATCHED_APPLICATION_ID_KEY);
  localStorage.removeItem(PROFILE_MATCHED_FILE_NAME_KEY);
  localStorage.removeItem(PROFILE_MATCHED_PROFILE_ID_KEY);
}

interface useProfileMatchingProps {
  onSuccess: () => void;
}

export const useProfileMatching = ({ onSuccess }: useProfileMatchingProps) => {
  const {
    jobPost,
    jobApplication,
    setJobApplication,
    setUserProfile,
    handleViewChange,
    setInterviewSessions,
    rounds,
  } = useCurrentApplication();
  const { setAiLoaderText, setIsShowAILoader, clearAILoader } = useAILoader();

  const navigate = useNavigate();

  const profileMatcherSerivce = new ProfileMatcherService();
  const jobApplicationService = new JobApplicationService();
  const interviewService = new InterviewService();

  const checkApplicationStatus = async (
    id: string,
    status: JobApplicationStatus,
  ): Promise<JobApplication> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const jobApplication =
            await jobApplicationService.findJobApplicationById(id);
          if (jobApplication && jobApplication.status === status) {
            clearInterval(interval);
            resolve(jobApplication);
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  const checkFirstRound = async (
    applicationId: string,
    firstRounId: string,
  ): Promise<InterviewSession[]> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const sessions =
            await interviewService.getAllInterviewSessionById(applicationId);
          if (sessions && sessions.length > 0) {
            const isDone =
              sessions[0].interviewRoundId === firstRounId &&
              sessions[0].isQuestionsGenerated;
            if (isDone) {
              clearInterval(interval);
              resolve(sessions);
            }
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  const checkAssessmentStatus = async (id: string): Promise<JobApplication> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const jobApplication =
            await jobApplicationService.findJobApplicationById(id);
          if (jobApplication && jobApplication.questionnaireStatus) {
            clearInterval(interval);
            resolve(jobApplication);
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 2000);
    });
  };

  const handleWithSelectedProfile = async (profile: Userprofile) => {
    if (!jobPost) {
      openNotification("error", "No job post found for matching profile.", "");
      return;
    }

    if (!jobApplication) {
      openNotification(
        "error",
        "No job application found for matching profile.",
        "",
      );
      return;
    }

    setIsShowAILoader(true);
    setAiLoaderText("Matching your profile with job description...");
    try {
      setProfileMatchStartedOnLocalStorage(
        jobPost._id,
        jobApplication._id,
        null,
        profile._id,
      );
      const profileMatchResult =
        await profileMatcherSerivce.matchResumeAgainstJobDescriptionByUsingExistingUserProfile(
          jobApplication._id,
          jobPost._id,
          profile._id,
        );
      if (!profileMatchResult) {
        clearProfileMatchTrackOnLocalStorage();
        openNotification("error", "Try Again", "Failed to match resume");
        clearAILoader();
        onSuccess();
        return;
      }
      if (!profileMatchResult.success) {
        clearProfileMatchTrackOnLocalStorage();
        openNotification("error", "Try Again", "Failed to match resume");
        clearAILoader();
        onSuccess();
        return;
      }
      clearProfileMatchTrackOnLocalStorage();

      const matchResult = profileMatchResult.data;
      const isOptionalProfileMatching =
        jobPost.isProfileMatchRequired === false;
      const isQualified = matchResult.status === "qualified";

      if (isQualified) {
        return await handleQualifiedProfileMatch(matchResult);
      } else {
        if (isOptionalProfileMatching) {
          return await handleQualifiedProfileMatch(matchResult);
        } else {
          return await handleUnqualifiedProfileMatch();
        }
      }
    } catch (error: any) {
      console.error(
        "Error in handling profile match with existing profile:",
        error,
      );
      openNotification(
        "error",
        "Failed to match profile with selected profile.",
        error.message || "",
      );
      clearAILoader();
      onSuccess();
      return;
    }
  };

  const handleWithSelectedFile = async (
    file: File,
    parsedResume: ParsedResumeContent,
  ) => {
    if (!jobPost) {
      openNotification("error", "No job post found for matching profile.", "");
      return;
    }

    if (!jobApplication) {
      openNotification(
        "error",
        "No job application found for matching profile.",
        "",
      );
      return;
    }

    setIsShowAILoader(true);
    setAiLoaderText("Uploading your resume...");
    try {
      // Step 1: Upload resume file to S3
      const fileNameInS3 =
        await profileMatcherSerivce.uploadResumeToS3AndMatchProfile(
          file,
          jobApplication?._id,
        );

      if (!fileNameInS3) {
        openNotification(
          "error",
          "Failed to upload resume and match profile.",
          "",
        );
        clearAILoader();
        onSuccess();
        return;
      }

      // Adding track variables
      setProfileMatchStartedOnLocalStorage(
        jobPost._id,
        jobApplication._id,
        fileNameInS3,
        null,
      );

      // Step 2: Match resume against job description
      setAiLoaderText("Matching your profile with job description...");

      const profileMatchResult =
        await profileMatcherSerivce.matchResumeAgainstJobDescriptionByCreatingNewUserProfile(
          jobApplication._id,
          jobPost._id,
          fileNameInS3,
          parsedResume,
        );
      if (!profileMatchResult) {
        clearProfileMatchTrackOnLocalStorage();
        openNotification("error", "Try Again", "Failed to match resume");
        clearAILoader();
        onSuccess();
        return;
      }
      if (!profileMatchResult.success) {
        clearProfileMatchTrackOnLocalStorage();
        openNotification("error", "Try Again", "Failed to match resume");
        clearAILoader();
        onSuccess();
        return;
      }
      clearProfileMatchTrackOnLocalStorage();

      const matchResult = profileMatchResult.data;
      const isOptionalProfileMatching =
        jobPost.isProfileMatchRequired === false;
      const isQualified = matchResult.status === "qualified";

      if (isQualified) {
        return await handleQualifiedProfileMatch(matchResult);
      } else {
        if (isOptionalProfileMatching) {
          return await handleQualifiedProfileMatch(matchResult);
        } else {
          return await handleUnqualifiedProfileMatch();
        }
      }
    } catch (error: any) {
      openNotification(
        "error",
        "Failed to match profile with selected file.",
        error.message || "",
      );
      clearAILoader();
      onSuccess();
      return;
    }
  };

  const handleProfileMatchRecovery = async () => {};

  const handleAIAssessmentEnabled = async (profileMatchResult: MatchResult) => {
    try {
      const firstRoundId = jobPost?.interviewRounds.find(
        (r) => r.interviewRoundNumber === 1,
      )?.interviewRoundId;
      if (!firstRoundId) {
        return null;
      }

      setAiLoaderText("Processing job application...");
      let updatedApplication = await checkAssessmentStatus(
        profileMatchResult.jobApplicationId,
      );

      if (updatedApplication.questionnaireStatus === "action_required") {
        const userProfileResponse =
          await profileMatcherSerivce.getUserProfileById(
            updatedApplication.profileId!,
          );

        setJobApplication(updatedApplication);
        setUserProfile(userProfileResponse);
        handleViewChange("result");
        return;
      }
      setAiLoaderText("Creating assessment...");
      const sessions = await checkFirstRound(
        profileMatchResult.jobApplicationId,
        firstRoundId,
      );

      const modifiedApplication =
        await jobApplicationService.findJobApplicationById(
          profileMatchResult.jobApplicationId,
        );
      if (modifiedApplication) {
        setJobApplication(modifiedApplication);
        const userProfileResponse =
          await profileMatcherSerivce.getUserProfileById(
            modifiedApplication.profileId!,
          );
        setUserProfile(userProfileResponse);
      }

      setInterviewSessions(sessions);
      const firstSession = sessions[0];
      const firstRound = rounds.find(
        (r) => r.round._id === firstSession.interviewRoundId,
      );
      if (firstRound?.round.answerType === "simulation") {
        navigate(`/${jobPost.slug}/simulation?sessionId=${firstSession._id}`);
      } else {
        navigate(`/${jobPost.slug}/interview-round?sessionId=${firstSession._id}`);
      }
    } catch (error: any) {
      console.error("Error", error);
    } finally {
      clearAILoader();
      onSuccess();
    }
  };

  const handleOnlineInterviewEnabled = async (
    profileMatchResult: MatchResult,
  ) => {
    try {
      setAiLoaderText("Validating job application...");
      const updatedApplication = await checkApplicationStatus(
        profileMatchResult.jobApplicationId,
        "interview",
      );
      const userProfileResponse =
        await profileMatcherSerivce.getUserProfileById(
          updatedApplication.profileId!,
        );
      setJobApplication(updatedApplication);
      setUserProfile(userProfileResponse);
      handleViewChange("interview");
    } catch (error: any) {
      console.error("Error", error);
    } finally {
      clearAILoader();
      onSuccess();
    }
  };

  const handleOnlineInterviewDisabled = async (
    profileMatchResult: MatchResult,
  ) => {
    try {
      setAiLoaderText("Validating job application...");
      const updatedApplication = await checkApplicationStatus(
        profileMatchResult.jobApplicationId,
        "shortlisted",
      );
      const userProfileResponse =
        await profileMatcherSerivce.getUserProfileById(
          updatedApplication.profileId!,
        );
      setJobApplication(updatedApplication);
      setUserProfile(userProfileResponse);
      handleViewChange("result");
    } catch (error: any) {
      console.error("Error", error);
    } finally {
      clearAILoader();
      onSuccess();
    }
  };

  const handleQualifiedProfileMatch = async (
    profileMatchResult: MatchResult,
  ) => {
    if (!jobPost) {
      openNotification("error", "No job post found for matching profile.", "");
      return;
    }

    if (!jobApplication) {
      openNotification(
        "error",
        "No job application found for matching profile.",
        "",
      );
      return;
    }
    const isAIAssessmentEnabled =
      jobPost.interviewRounds && jobPost.interviewRounds.length > 0;
    const isOnlineInterviewEnabled = jobPost.onlineInterview === true;

    if (isAIAssessmentEnabled) {
      return await handleAIAssessmentEnabled(profileMatchResult);
    }

    if (isOnlineInterviewEnabled) {
      return await handleOnlineInterviewEnabled(profileMatchResult);
    } else {
      return await handleOnlineInterviewDisabled(profileMatchResult);
    }
  };

  const handleUnqualifiedProfileMatch = async () => {
    if (!jobPost) {
      openNotification("error", "No job post found for matching profile.", "");
      return;
    }

    if (!jobApplication) {
      openNotification(
        "error",
        "No job application found for matching profile.",
        "",
      );
      return;
    }

    try {
      setAiLoaderText("Validating profile match result...");
      const updatedJobApplication = await checkApplicationStatus(
        jobApplication._id,
        "rejected",
      );
      if (updatedJobApplication) {
        const userProfileResponse =
          await profileMatcherSerivce.getUserProfileById(
            updatedJobApplication.profileId!,
          );
        if (userProfileResponse) {
          setUserProfile(userProfileResponse);
        }
        setJobApplication(updatedJobApplication);
        handleViewChange("result");
      } else {
        console.error(
          "Failed to fetch updated job application after profile match. Please check the server logs for more details.",
        );
      }
    } catch (error: any) {
      console.error("Error in handling unqualified profile match:", error);
    } finally {
      clearAILoader();
      onSuccess();
    }
  };

  return {
    handleWithSelectedProfile,
    handleWithSelectedFile,
    handleProfileMatchRecovery,
  };
};
